'use client';

import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  albumApi,
  type AlbumCostPredictionParams,
} from '../api/album-service';

/**
 * Server-predicted token cost of an album generation
 * (`GET /album/cost-prediction`). The server also computes the
 * significant-cost flag (its threshold lives in server config) and the
 * localized warning text — the client never compares tokens to a threshold
 * and never composes warning copy (API-driven state flags / messages rules).
 * `tokens` + `isSignificantCost` drive the Generate-button `~N 🥜` suffix;
 * `warningMessage` feeds the warning NoticeBanner above the Generate area.
 *
 * `params` MUST be a stable/memoized object: it is debounced (400ms) so a
 * burst of toggle flips fires one request, and each settings combination is
 * its own query key (cached 5 min). No placeholder/previous data on purpose —
 * while a new combination is in flight (or on error) everything is
 * undefined/null and the button simply shows no suffix / no banner, which
 * also rules out a stale price flashing for the wrong settings.
 */
export function useAlbumCostPrediction(
  conceptId: string,
  params: AlbumCostPredictionParams
) {
  const debouncedParams = useDebounce(params, 400);

  // The price leaves out what a finished album of this design already made,
  // so it changes when one finishes (or a version is restored). Keying on the
  // finished albums reuses the albums list's own reconcile points instead of
  // invalidating the price from every place an album can finish.
  const { data: albums } = useQuery({
    queryKey: cacheKeys.albums.list(conceptId),
    queryFn: () => albumApi.list(conceptId),
    enabled: !!conceptId,
    staleTime: 5 * 60 * 1000,
  });
  const finishedAlbums = (albums ?? [])
    .filter((album) => album.status === 'completed')
    .map((album) => album.id)
    .sort()
    .join(',');

  const { data } = useQuery({
    queryKey: [
      ...cacheKeys.albumCostPrediction.all,
      conceptId,
      debouncedParams,
      finishedAlbums,
    ],
    queryFn: () => albumApi.costPrediction(conceptId, debouncedParams),
    enabled: !!conceptId && debouncedParams.renders_count > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    tokens: data?.tokens,
    isSignificantCost: data?.is_significant_cost,
    warningMessage: data?.warning_message ?? null,
  };
}
